import { createSlice } from "@reduxjs/toolkit";
import { RootState, StoreDispatch } from "./store";
import { getUserName, getUserApprovers } from "src/network/graphql/userService";
import { isUUID } from "src/utils";

const userNamesSlice = createSlice({
  name: "userNames",
  initialState: {
    cache: {} as Record<string, string>,
    resolvingIds: [] as string[],
    // Per-user approver overrides (user_id -> own approver ids), for the Users
    // table Approver column. Empty array = fetched, no personal override.
    approversCache: {} as Record<string, string[]>,
    // Same shape for the second reviewer. The query has always fetched both;
    // only approvers were being kept, so anything needing the verifier had to
    // re-fetch the user.
    verifiersCache: {} as Record<string, string[]>,
    approversResolving: [] as string[]
  },
  reducers: {
    setUserNames: (state, action: { payload: Record<string, string> }) => {
      const entries = action.payload;

      Object.entries(entries).forEach(([userId, userName]) => {
        state.cache[userId] = userName;
      });
      state.resolvingIds = state.resolvingIds.filter((id) => !(id in entries));
    },
    addResolvingIds: (state, action: { payload: string[] }) => {
      const newIds = action.payload.filter((id) => !state.resolvingIds.includes(id));

      state.resolvingIds = [...state.resolvingIds, ...newIds];
    },
    setUserApprovers: (
      state,
      action: { payload: Record<string, { approvers: string[]; verifiers: string[] }> }
    ) => {
      const entries = action.payload;

      Object.entries(entries).forEach(([userId, reviewers]) => {
        state.approversCache[userId] = reviewers.approvers;
        state.verifiersCache[userId] = reviewers.verifiers;
      });
      state.approversResolving = state.approversResolving.filter((id) => !(id in entries));
    },
    addApproversResolving: (state, action: { payload: string[] }) => {
      const newIds = action.payload.filter((id) => !state.approversResolving.includes(id));

      state.approversResolving = [...state.approversResolving, ...newIds];
    }
  }
});

const { setUserNames, addResolvingIds, setUserApprovers, addApproversResolving } = userNamesSlice.actions;

export const resolveUserNamesAction = (userIds: string[]) => {
  return async (dispatch: StoreDispatch, getState: () => RootState) => {
    const { cache, resolvingIds } = getState().userNames;
    const currentUserId = getState().auth.userInfo?.id;

    const uniqueIds = Array.from(new Set(userIds));
    const idsToProcess: string[] = [];
    const immediateUpdates: Record<string, string> = {};

    for (const userId of uniqueIds) {
      if (!userId || cache[userId]) continue;

      if (userId === "SYSTEM") continue;

      if (currentUserId && userId === currentUserId) {
        immediateUpdates[userId] = "You";
        continue;
      }

      if (!isUUID(userId)) {
        immediateUpdates[userId] = "Unknown";
        continue;
      }

      if (!resolvingIds.includes(userId)) {
        idsToProcess.push(userId);
      }
    }

    if (Object.keys(immediateUpdates).length > 0) {
      dispatch(setUserNames(immediateUpdates));
    }

    if (idsToProcess.length === 0) return;

    dispatch(addResolvingIds(idsToProcess));

    const results = await Promise.all(
      idsToProcess.map((userId) =>
        getUserName(userId)
          .then((res) => ({ userId, name: res.data?.get_user_name ?? "Unknown" }))
          .catch(() => ({ userId, name: "Unknown" }))
      )
    );

    const resolved = Object.fromEntries(results.map((r) => [r.userId, r.name]));

    dispatch(setUserNames(resolved));
  };
};

// Fetch each user's own approver ids (their personal override) for the Users
// table. The Approver column shows these when present, otherwise the org's
// universal default_approvers.
export const resolveUserApproversAction = (userIds: string[]) => {
  return async (dispatch: StoreDispatch, getState: () => RootState) => {
    const { approversCache, approversResolving } = getState().userNames;
    const idsToFetch = Array.from(new Set(userIds)).filter(
      (id) => id && !(id in approversCache) && !approversResolving.includes(id)
    );

    if (idsToFetch.length === 0) return;

    dispatch(addApproversResolving(idsToFetch));

    const results = await Promise.all(
      idsToFetch.map((id) =>
        getUserApprovers(id)
          .then((res) => ({
            id,
            approvers: (res.data?.get_user?.preferences?.approvers ?? []) as string[],
            verifiers: (res.data?.get_user?.preferences?.verifiers ?? []) as string[]
          }))
          .catch(() => ({ id, approvers: [] as string[], verifiers: [] as string[] }))
      )
    );

    dispatch(
      setUserApprovers(
        Object.fromEntries(results.map((r) => [r.id, { approvers: r.approvers, verifiers: r.verifiers }]))
      )
    );
  };
};

export default userNamesSlice.reducer;
